import { useEffect, useState } from "react";

import type { SimklClient } from "@/api/client";
import type { SearchResult } from "@/api/schemas";
import { SearchIcon } from "@/components/Icon";
import { posterUrl } from "@/lib/poster";

const DEBOUNCE_MS = 300;
const MIN_QUERY = 2;

function Result({
  result,
  inLibrary,
  onAdd,
  onOpen,
}: {
  result: SearchResult;
  inLibrary: boolean;
  onAdd: () => void;
  onOpen: () => void;
}) {
  const poster = posterUrl(result.poster, "row");
  const detail = [
    result.year,
    result.episodeCount === null ? null : `${result.episodeCount} episodes`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <li className="row">
      <button type="button" className="row-fg" onClick={onOpen}>
        {poster === null ? (
          <span className="poster" />
        ) : (
          <img className="poster" src={poster} alt="" loading="lazy" decoding="async" />
        )}
        <span className="meta">
          <span className="show-name">{result.title}</span>
          <span className="sub">{detail}</span>
        </span>
        <span
          role="button"
          tabIndex={inLibrary ? -1 : 0}
          className="result-add"
          data-added={inLibrary ? "1" : "0"}
          aria-disabled={inLibrary}
          onClick={(event) => {
            // The row opens the sheet; Add must not do both.
            event.stopPropagation();
            if (!inLibrary) onAdd();
          }}
        >
          {inLibrary ? "Added" : "Add"}
        </span>
      </button>
    </li>
  );
}

function Results({
  query,
  results,
  searching,
  knownIds,
  added,
  onAdd,
  onPreview,
}: {
  query: string;
  results: readonly SearchResult[];
  searching: boolean;
  knownIds: ReadonlySet<number>;
  added: ReadonlySet<number>;
  onAdd: (result: SearchResult) => void;
  onPreview: (result: SearchResult) => void;
}) {
  if (query.trim().length < MIN_QUERY) {
    return (
      <div className="empty">
        <strong>Find a show</strong>
        <p>Type at least two characters.</p>
      </div>
    );
  }
  if (results.length === 0) {
    return (
      <div className="empty">
        <strong>{searching ? "Searching…" : "Nothing found"}</strong>
        <p>{searching ? "" : "Try a different spelling."}</p>
      </div>
    );
  }
  return (
    <ul className="rows">
      {results.map((result) => (
        <Result
          key={result.ids.simkl}
          result={result}
          inLibrary={knownIds.has(result.ids.simkl) || added.has(result.ids.simkl)}
          onAdd={() => {
            onAdd(result);
          }}
          onOpen={() => {
            onPreview(result);
          }}
        />
      ))}
    </ul>
  );
}

export function Search({
  client,
  knownIds,
  onAdd,
  onPreview,
}: {
  client: SimklClient;
  knownIds: ReadonlySet<number>;
  onAdd: (result: SearchResult) => void;
  onPreview: (result: SearchResult) => void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<readonly SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [added, setAdded] = useState<ReadonlySet<number>>(new Set());

  useEffect(() => {
    const trimmed = query.trim();
    // Too short to search. The change handler already cleared stale results,
    // so this effect has no state to touch on the way out.
    if (trimmed.length < MIN_QUERY) return;

    const controller = new AbortController();
    // Debounced: a request per keystroke would burn the rate budget and land
    // out of order.
    const timer = setTimeout(() => {
      setSearching(true);
      client
        .search(trimmed, controller.signal)
        .then((found) => {
          if (!controller.signal.aborted) setResults(found);
        })
        .catch(() => {
          if (!controller.signal.aborted) setResults([]);
        })
        .finally(() => {
          if (!controller.signal.aborted) setSearching(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [client, query]);

  return (
    <>
      <div className="searchbar">
        <SearchIcon />
        <input
          type="search"
          value={query}
          placeholder="Search shows"
          autoComplete="off"
          onChange={(event) => {
            const next = event.target.value;
            setQuery(next);
            if (next.trim().length < MIN_QUERY) setResults([]);
          }}
        />
      </div>
      <Results
        query={query}
        results={results}
        searching={searching}
        knownIds={knownIds}
        added={added}
        onAdd={(result) => {
          setAdded(new Set([...added, result.ids.simkl]));
          onAdd(result);
        }}
        onPreview={onPreview}
      />
    </>
  );
}
